import { CLASS_NAMES, EVENTS, SELECTORS, MISC } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the EmbedForm module
 */
export default class EmbedForm extends MessageBus {
  /**
   * Constructor function of the EmbedForm class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;
    this.init();
  }

  /**
   * Initializes the view by calling functions to cache DOM references,
   * setup event handlers, and enable the form functionalities.
   * @returns {object} EmbedForm A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().appendErrorDivs().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   * @returns {object} EmbedForm A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.requestType = this.element.querySelector(SELECTORS.TYPE);
    this.labelRequestType = this.element.querySelector(`
      .${CLASS_NAMES.LABEL_TYPE}`);
    this.topic = this.element.querySelector(SELECTORS.TOPIC);
    this.labelTopic = this.element.querySelector(`.${CLASS_NAMES.LABEL_TOPIC}`);
    this.caseReason = this.element.querySelector(SELECTORS.CASE_REASON);
    this.labelCaseReason = this.element.querySelector(`
      .${CLASS_NAMES.LABEL_CASE_REASON}`);
    this.topicMapping =
      this.element.querySelector(SELECTORS.TOPIC_MAPPING)?.value ?? '';

    this.textFieldsError = true;
    this.emailFieldError = true;
    this.phoneFieldsError = false;

    return this;
  }

  /**
   * Appends missing error-text divs to required fields.
   * @returns {object} EmbedForm A reference to the error
   */
  appendErrorDivs() {
    [
      SELECTORS.NAME,
      SELECTORS.DESCRIPTION,
      SELECTORS.EMAIL_ID,
      SELECTORS.PHONE
    ].forEach((selector) => this.appendErrorDiv(selector));

    // Captcha error message
    const recaptcha = this.element.querySelector(`.${CLASS_NAMES.G_RECAPTCHA}`);
    if (
      recaptcha &&
      !this.element.querySelector(`.${CLASS_NAMES.CAPTCHA_ERROR_TEXT}`)
    ) {
      const captchaError = document.createElement(SELECTORS.DIV);
      captchaError.classList.add(`${CLASS_NAMES.CAPTCHA_ERROR_TEXT}`);
      captchaError.textContent =
        'Please validate the reCAPTCHA field before submitting';
      recaptcha.after(captchaError);
    }

    return this;
  }

  /**
   * Appends an error-text div if it's missing.
   * @param {string} selector - The input selector
   */
  appendErrorDiv(selector) {
    const field = this.element.querySelector(selector);
    if (
      field &&
      !field.parentElement.querySelector(`.${CLASS_NAMES.ERROR_TEXT}`)
    ) {
      const errorDiv = document.createElement(SELECTORS.DIV);
      errorDiv.classList.add(`${CLASS_NAMES.ERROR_TEXT}`);
      field.parentElement.appendChild(errorDiv);
    }
  }

  /**
   * Setup event handlers for form validation and interactions
   * @returns {object} EmbedForm A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.requestType?.addEventListener(EVENTS.CHANGE, () =>
      this.handleRequestTypeChange()
    );
    this.topic?.addEventListener(EVENTS.CHANGE, () => this.handleTopicChange());
    this.caseReason?.addEventListener(EVENTS.CHANGE, (event) => {
      const selectedValue = event.target.value; // Get selected option value
      this.caseReason.value = selectedValue; // Ensure it remains selected
    });

    this.element
      .querySelector(SELECTORS.FORM)
      ?.addEventListener(EVENTS.SUBMIT, (e) => this.handleSubmit(e));

    this.setupDropdownToggles();
    this.setupCheckboxToggles();

    return this;
  }

  /**
   * Enable form functionalities
   * @returns {object} EmbedForm A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.addRequiredIndicators();
    this.validateTextFields(SELECTORS.NAME);
    this.validateTextFields(SELECTORS.DESCRIPTION);
    this.validateEmailField(SELECTORS.EMAIL_ID);
    this.validateContactNumberField(SELECTORS.PHONE);
    return this;
  }

  /**
   * Adds required indicators (*) to labels
   */
  addRequiredIndicators() {
    [
      CLASS_NAMES.TOPIC,
      CLASS_NAMES.CASE_REASON,
      CLASS_NAMES.CASE_REASON_2
    ].forEach((id) => {
      const label = this.element.querySelector(`label[for="${id}"]`);
      if (label) label.innerHTML += '<span class="required-indicator">*</span>';
    });

    this.requestType?.setAttribute(CLASS_NAMES.REQUIRED, MISC.TRUE);
    this.labelRequestType.innerHTML +=
      '<span class="required-indicator">*</span>';
  }

  /**
   * Handles request type change to show/hide related dropdowns
   */
  handleRequestTypeChange() {
    const selectedRequestType =
      this.requestType?.options[this.requestType.selectedIndex].text;
    this.addRemoveClass(
      selectedRequestType,
      'Update/Confirm Donation Details',
      this.topic,
      this.labelTopic,
      CLASS_NAMES.UPDATE_DONATION_SELECTED
    );

    if (selectedRequestType !== 'Update/Confirm Donation Details') {
      this.caseReason.classList.remove(CLASS_NAMES.CANCEL_DONATION_SELECTED);
      this.labelCaseReason.classList.remove(
        CLASS_NAMES.CANCEL_DONATION_SELECTED
      );
      this.topic.value = '';
      this.caseReason.value = '';
    }
  }

  /**
   * Handles topic change to show/hide related dropdowns
   */
  handleTopicChange() {
    const selectedTopic = this.topic?.options[this.topic.selectedIndex].text;
    this.addRemoveClass(
      selectedTopic,
      'Update Gift Amount/Frequency',
      this.caseReason,
      this.labelCaseReason,
      CLASS_NAMES.CANCEL_DONATION_SELECTED,
      'Cancel Donation'
    );
  }

  /**
   * Show/hide dropdowns depending on previous dropdown selection
   * @param {object} selected - The selected dropdown
   * @param {object} expected - The expected dropdown
   * @param {object} dropdown - The input selector
   * @param {string} dropdownLabel - Dropdown Label
   * @param {string} className - Classname
   * @param {object} secondExpected - The second expected dropdown
   */
  addRemoveClass(
    selected,
    expected,
    dropdown,
    dropdownLabel,
    className,
    secondExpected
  ) {
    if (!dropdown || !dropdownLabel) return;

    if (selected === expected || selected === secondExpected) {
      dropdown.classList.add(className);
      dropdown.setAttribute(CLASS_NAMES.REQUIRED, MISC.TRUE);
      dropdownLabel.classList.add(className);
    } else {
      dropdown.classList.remove(className);
      dropdownLabel.classList.remove(className);
    }

    // Store dropdown state in class property if needed
    this.lastDropdownState = selected;
  }

  /**
   * Setup dropdown toggles to change arrow appearance
   */
  setupDropdownToggles() {
    [this.requestType, this.topic, this.caseReason].forEach((dropdown) => {
      dropdown?.addEventListener(EVENTS.CLICK, (e) => {
        e.stopPropagation();
        dropdown.classList.toggle(CLASS_NAMES.OPENED);
      });
    });

    document.addEventListener(EVENTS.CLICK, () => {
      [this.requestType, this.topic, this.caseReason].forEach((dropdown) => {
        dropdown?.classList.remove(CLASS_NAMES.OPENED);
      });
    });
  }

  /**
   * Setup checkbox toggles
   */
  setupCheckboxToggles() {
    this.element
      .querySelectorAll(
        `.${CLASS_NAMES.EMBED_FORM} ${SELECTORS.FORM} ${SELECTORS.PARAGRAPH} .${SELECTORS.CHECKBOX}`
      )
      .forEach((checkbox) =>
        checkbox.addEventListener(EVENTS.CLICK, () =>
          checkbox.classList.toggle(CLASS_NAMES.CHECKED)
        )
      );
  }

  /**
   * Handles form submission and validation
   * @param {Event} e - Form submit event
   */
  handleSubmit(e) {
    this.validateTextFields(SELECTORS.NAME);
    this.validateTextFields(SELECTORS.DESCRIPTION);
    this.validateEmailField(SELECTORS.EMAIL_ID);
    this.validateContactNumberField(SELECTORS.PHONE);

    const recaptcha = this.element.querySelector(
      '#g-recaptcha-response'
    )?.value;
    if (!recaptcha) {
      this.element
        .querySelector(`.${CLASS_NAMES.CAPTCHA_ERROR_TEXT}`)
        ?.classList.add(CLASS_NAMES.ERROR);
    } else {
      this.element
        .querySelector(`.${CLASS_NAMES.CAPTCHA_ERROR_TEXT}`)
        ?.classList.remove(CLASS_NAMES.ERROR);
    }

    const isFormInvalid =
      this.textFieldsError ||
      this.emailFieldError ||
      this.phoneFieldsError ||
      !recaptcha;
    if (isFormInvalid) {
      e.preventDefault();
    }
  }

  /**
   * Validates text fields
   * @param {string} selector - The input selector
   */
  validateTextFields(selector) {
    const field = this.element.querySelector(selector);
    if (!field) return;

    const label = this.element.querySelector(`label[for="${field.id}"]`);
    field.setAttribute(CLASS_NAMES.REQUIRED, MISC.TRUE);

    field.addEventListener(EVENTS.BLUR, () => {
      const value = field.value.trim();
      const errorText = field.parentElement?.querySelector(
        `.${CLASS_NAMES.ERROR_TEXT}`
      );

      if (!value) {
        field.classList.add(CLASS_NAMES.INVALID);
        field.classList.remove(CLASS_NAMES.VALID);
        label?.classList.add(CLASS_NAMES.INVALID);
        label?.classList.remove(CLASS_NAMES.VALID);

        errorText.textContent =
          selector === SELECTORS.NAME
            ? 'Please enter your name'
            : 'This field cannot be empty';
        this.textFieldsError = true;
      } else {
        field.classList.add(CLASS_NAMES.VALID);
        field.classList.remove(CLASS_NAMES.INVALID);
        label?.classList.add(CLASS_NAMES.VALID);
        label?.classList.remove(CLASS_NAMES.INVALID);

        errorText.textContent = '';
        this.textFieldsError = false;
      }
    });
  }

  /**
   * Validates email field
   * @param {string} selector - The input selector
   */
  validateEmailField(selector) {
    const field = this.element.querySelector(selector);
    if (!field) return;

    const label = this.element.querySelector(`label[for="${field.id}"]`);
    field.setAttribute(CLASS_NAMES.REQUIRED, MISC.TRUE);

    field.addEventListener(EVENTS.BLUR, () => {
      const value = field.value.trim();
      const errorText = field.parentElement?.querySelector(
        `.${CLASS_NAMES.ERROR_TEXT}`
      );
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

      if (!value) {
        field.classList.add(CLASS_NAMES.INVALID);
        field.classList.remove(CLASS_NAMES.VALID);
        label?.classList.add(CLASS_NAMES.INVALID);
        label?.classList.remove(CLASS_NAMES.VALID);

        errorText.textContent = 'Please enter your email';
        this.emailFieldError = true;
      } else if (!isValidEmail) {
        field.classList.add(CLASS_NAMES.INVALID);
        field.classList.remove(CLASS_NAMES.VALID);
        label?.classList.add(CLASS_NAMES.INVALID);
        label?.classList.remove(CLASS_NAMES.VALID);

        errorText.textContent = 'Please enter a valid email address';
        this.emailFieldError = true;
      } else {
        field.classList.add(CLASS_NAMES.VALID);
        field.classList.remove(CLASS_NAMES.INVALID);
        label?.classList.add(CLASS_NAMES.VALID);
        label?.classList.remove(CLASS_NAMES.INVALID);

        errorText.textContent = '';
        this.emailFieldError = false;
      }
    });
  }

  /**
   * Validates phone number field
   * @param {string} selector - The input selector
   */
  validateContactNumberField(selector) {
    const field = this.element.querySelector(selector);
    if (!field) return;

    const label = this.element.querySelector(`label[for="${field.id}"]`);
    field.setAttribute(CLASS_NAMES.REQUIRED, MISC.TRUE);

    field.addEventListener(EVENTS.BLUR, () => {
      const value = field.value.trim();
      const errorText = field.parentElement?.querySelector(
        `.${CLASS_NAMES.ERROR_TEXT}`
      );
      const isValidPhone = /^[0-9]{10}$/.test(value);

      if (!value) {
        field.classList.add(CLASS_NAMES.INVALID);
        field.classList.remove(CLASS_NAMES.VALID);
        label?.classList.add(CLASS_NAMES.INVALID);
        label?.classList.remove(CLASS_NAMES.VALID);

        errorText.textContent = 'Please enter your phone number';
        this.phoneFieldsError = true;
      } else if (!isValidPhone) {
        field.classList.add(CLASS_NAMES.INVALID);
        field.classList.remove(CLASS_NAMES.VALID);
        label?.classList.add(CLASS_NAMES.INVALID);
        label?.classList.remove(CLASS_NAMES.VALID);

        errorText.textContent = 'Please enter a valid contact number';
        this.phoneFieldsError = true;
      } else {
        field.classList.add(CLASS_NAMES.VALID);
        field.classList.remove(CLASS_NAMES.INVALID);
        label?.classList.add(CLASS_NAMES.VALID);
        label?.classList.remove(CLASS_NAMES.INVALID);

        errorText.textContent = '';
        this.phoneFieldsError = false;
      }
    });
  }
}
